'use client'

import { useState, useCallback } from 'react'
import { useTranslations } from 'next-intl'
import cn from 'classnames'

import { useBookingActionsCtx, useBookingState } from '@booking/state/booking.context'
import type { ExtraQuantities } from '@booking/types/booking'

// Reuse the existing list + control styles for visual consistency
import listStyles from '@booking/stages/QuantitySelection/index.module.scss'
import controlStyles from '@booking/stages/QuantitySelection/components/TicketType/index.module.scss'
import extrasStyles from '@booking/stages/QuantitySelection/components/ExtrasSection/index.module.scss'
import MinusIcon from '@booking/stages/QuantitySelection/components/TicketType/minus-icon.svg'
import PlusIcon from '@booking/stages/QuantitySelection/components/TicketType/plus-icon.svg'

import { DAYS } from '../rock-in-rio-days'

// ---------------------------------------------------------------------------

export default function RockInRioExtrasRows() {
  const t = useTranslations('booking')
  const state = useBookingState()
  const { dispatch } = useBookingActionsCtx()

  const extrasTotal = state.extras.reduce((sum, e) => sum + e.quantity, 0)
  const maxExtrasTotal = state.config.extras?.maxExtrasTotal
  const totalMaxReached = typeof maxExtrasTotal === 'number' && extrasTotal >= maxExtrasTotal

  const setExtraValue = useCallback(
    (category: string, nextQty: number) => {
      const existing = state.extras.find((e) => e.category === category)
      let next: ExtraQuantities

      if (existing) {
        next = state.extras.map((e) =>
          e.category === category ? { ...e, quantity: nextQty } : e,
        )
      } else {
        next = [...state.extras, { category, quantity: nextQty }]
      }

      dispatch({ type: 'SET_EXTRAS', extras: next })
    },
    [state.extras, dispatch],
  )

  return (
    <div className={listStyles.boxComandsWrapper}>
      <ul className={listStyles.ticketTypes}>
        {DAYS.map((day) => {
          const value = state.extras.find((e) => e.category === day.category)?.quantity ?? 0
          return (
            <li key={day.category} className={cn(day.disabled && extrasStyles.disabled)}>
              <div className={extrasStyles.richLabel}>
                {day.tagKey && <span className={extrasStyles.tag}>{t(day.tagKey)}</span>}
                <strong className={extrasStyles.title}>{t(day.titleKey)}</strong>
                {day.price && (
                  <span className={extrasStyles.price}>
                    {day.price} {t('extras.perPerson')}
                  </span>
                )}
                {day.moreInfoKey && <MoreInfoPopover contentKey={day.moreInfoKey} />}
              </div>

              <span className={controlStyles.controlsHolder}>
                <button
                  type="button"
                  className={cn(controlStyles.buttonMath, controlStyles.buttonMinus)}
                  disabled={value <= 0}
                  onClick={() => setExtraValue(day.category, value - 1)}
                  aria-label={`Decrease ${day.category}`}
                >
                  <MinusIcon />
                </button>
                <span className={controlStyles.controlValue}>{value}</span>
                <button
                  type="button"
                  className={cn(controlStyles.buttonMath, controlStyles.buttonPlus)}
                  disabled={totalMaxReached}
                  onClick={() => setExtraValue(day.category, value + 1)}
                  aria-label={`Increase ${day.category}`}
                >
                  <PlusIcon />
                </button>
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

// ---------------------------------------------------------------------------

function MoreInfoPopover({ contentKey }: { contentKey: string }) {
  const t = useTranslations('booking')
  const [open, setOpen] = useState(false)
  const lines = t(contentKey).split('\n').filter(Boolean)

  return (
    <span
      className={extrasStyles.moreInfoWrapper}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <span className={extrasStyles.moreInfoTrigger}>{t('extras.moreInfo')}</span>
      {open && (
        <div className={extrasStyles.popover}>
          {lines.map((line, i) => (
            <p key={i} className={extrasStyles.popoverLine}>
              {line}
            </p>
          ))}
        </div>
      )}
    </span>
  )
}
